import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import Form from '@cloudscape-design/components/form';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Input from '@cloudscape-design/components/input';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import Table from '@cloudscape-design/components/table';
import Textarea from '@cloudscape-design/components/textarea';
import api, { resolveBackendAssetUrl } from '../services/apiService';
import { formatDateDe } from '../utils/date';

const weekendWorkLabels = {
  none: 'Nein',
  saturday: 'Samstag',
  sunday: 'Sonntag',
  both: 'Samstag und Sonntag',
};

const insurancePricingOptions = [
  { value: 'percent_of_rent', label: 'Prozent vom Mietpreis' },
  { value: 'flat_per_day', label: 'Pauschal pro Versicherungstag' },
];

const priceBindingOptions = [
  { value: '12', label: '12 Stunden' },
  { value: '24', label: '24 Stunden' },
  { value: '48', label: '2 Tage' },
  { value: '96', label: '4 Tage' },
  { value: '168', label: '7 Tage' },
  { value: '336', label: '14 Tage' },
];

function priceBindingLabel(hours) {
  return priceBindingOptions.find((option) => option.value === String(hours))?.label || '24 Stunden';
}

function parsePrice(value) {
  let text = String(value || '').trim().replace(/\s/g, '');
  if (!text) return null;
  if (text.includes(',')) text = text.replaceAll('.', '').replace(',', '.');
  const price = Number(text);
  return Number.isFinite(price) && price >= 0 ? price : null;
}

function formatCurrency(value) {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(value));
}

function lineTotal(row, rows) {
  if (row.lineType === 'insurance') {
    if (!['rental_days', 'calendar_days'].includes(row.calculationBasis)) return null;
    if (!['percent_of_rent', 'flat_per_day'].includes(row.pricingMode)) return null;
    const rate = parsePrice(row.rateValue);
    if (rate === null || (row.pricingMode === 'percent_of_rent' && rate > 100)) return null;
    const days = row.calculationBasis === 'calendar_days' ? row.calendarDays : row.rentalDays;
    if (row.pricingMode === 'flat_per_day') return rate * Number(row.quantity) * Number(days);
    const rentalRow = rows.find((candidate) => (
      candidate.lineType === 'rental' && candidate.requestItemId === row.requestItemId
    ));
    const rentalUnitPrice = parsePrice(rentalRow?.unitPriceNet);
    if (rentalUnitPrice === null || rentalUnitPrice === 0) return null;
    return rentalUnitPrice * Number(row.quantity) * Number(days) * rate / 100;
  }
  const unitPrice = parsePrice(row.unitPriceNet);
  return unitPrice === null ? null : unitPrice * Number(row.multiplier);
}

export default function OfferPage() {
  const { token } = useParams();
  const [invitation, setInvitation] = useState(null);
  const [priceRows, setPriceRows] = useState([]);
  const [priceBindingHours, setPriceBindingHours] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    api.get(`/offers/${encodeURIComponent(token)}`)
      .then((response) => {
        setInvitation(response.data);
        setPriceRows((response.data.offerLines || []).map((line) => ({
          ...line,
          unitPriceNet: '',
          calculationBasis: line.lineType === 'insurance' ? '' : null,
          pricingMode: line.lineType === 'insurance' ? '' : 'unit_price',
          rateValue: '',
        })));
      })
      .catch((requestError) => setError(requestError.response?.data?.error || 'Der Preisangebotslink ist ungültig.'))
      .finally(() => setLoading(false));
  }, [token]);

  const priceSummary = useMemo(() => {
    let total = 0;
    for (const row of priceRows) {
      const rowTotal = lineTotal(row, priceRows);
      if (rowTotal === null || (row.lineType === 'rental' && rowTotal === 0)) {
        return { valid: false, total: null };
      }
      total += rowTotal;
    }
    return { valid: priceRows.length > 0, total };
  }, [priceRows]);

  function updateLine(key, changes) {
    setPriceRows((current) => current.map((row) => (
      row.key === key ? { ...row, ...changes } : row
    )));
  }

  async function submit(event) {
    event.preventDefault();
    setError('');
    if (!priceSummary.valid) {
      setError('Bitte für jede Position einen gültigen Einzelpreis eingeben. Mietpreise müssen größer als 0,00 € sein; Zusatzpositionen dürfen 0,00 € betragen.');
      return;
    }
    if (!priceBindingOptions.some((option) => option.value === priceBindingHours)) {
      setError('Bitte auswählen, wie lange Ihre Preise gebunden sind.');
      return;
    }
    setSubmitting(true);
    try {
      const response = await api.post(`/offers/${encodeURIComponent(token)}`, {
        lines: priceRows.map((row) => row.lineType === 'insurance'
          ? {
            key: row.key,
            calculationBasis: row.calculationBasis,
            pricingMode: row.pricingMode,
            rateValue: row.rateValue,
          }
          : { key: row.key, unitPriceNet: row.unitPriceNet }),
        message,
        priceBindingHours: Number(priceBindingHours),
      });
      setSuccess(response.data);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Das Preisangebot konnte nicht übermittelt werden.');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <div className="offer-shell offer-shell-wide"><Spinner size="large" /> Anfrage wird geladen …</div>;
  if (error && !invitation) return <div className="offer-shell offer-shell-wide"><Alert type="error" header="Preisangebotslink nicht verfügbar">{error}</Alert></div>;
  if (success) return (
    <div className="offer-shell offer-shell-wide">
      <Alert
        type={success.emailDelivery?.tenantContact ? 'success' : 'warning'}
        header="Preisangebot übermittelt"
      >
        {success.emailDelivery?.tenantContact
          ? `Das Preisangebot über ${formatCurrency(success.priceNet)} netto wurde mit einer Preisbindung von ${priceBindingLabel(success.priceBindingHours)} übermittelt. Die Preisangebotsabgabe wurde per E-Mail bestätigt.`
          : 'Ihr Preisangebot wurde gespeichert. Die Bestätigungs-E-Mail konnte jedoch nicht zugestellt werden. Bitte wenden Sie sich an den SmartRent-Administrator.'}
      </Alert>
    </div>
  );

  const request = invitation.request;
  return (
    <div className="offer-shell offer-shell-wide">
      <SpaceBetween size="l">
        <Container header={<Header variant="h1">Mietanfrage {request.reference}</Header>}>
          <SpaceBetween size="s">
            <div><strong>Vermieter:</strong> {invitation.tenantName}</div>
            <div><strong>Transport:</strong> {request.transportMode === 'delivery_pickup' ? 'Anlieferung / Abholung' : 'Selbstabholer'}</div>
            <div>
              <strong>{request.transportMode === 'delivery_pickup' ? 'Einsatzadresse' : 'Einsatzort'}:</strong>{' '}
              {[request.streetAddress, `${request.postalCode} ${request.city}`].filter(Boolean).join(', ')}
            </div>
            <div><strong>Zeitraum:</strong> {formatDateDe(request.startDate)} bis {formatDateDe(request.endDate)}</div>
            <div><strong>Miettage:</strong> {request.rentalDays}</div>
            <div><strong>Arbeit am Wochenende:</strong> {weekendWorkLabels[request.weekendWorkMode] || 'Nein'}</div>
            {request.message && <div><strong>Hinweis zum Einsatz:</strong> {request.message}</div>}
          </SpaceBetween>
        </Container>

        {!invitation.canSubmit ? (
          <Alert type={invitation.reason === 'already_submitted' ? 'success' : 'warning'} header="Keine weitere Eingabe möglich">
            {invitation.reason === 'already_submitted' && 'Sie haben für diese Anfrage bereits ein Preisangebot abgegeben.'}
            {invitation.reason === 'other_offer_selected' && 'Der Anfrager hat sich bereits für ein anderes Preisangebot entschieden.'}
            {invitation.reason === 'expired' && 'Dieser Preisangebotslink ist abgelaufen.'}
            {invitation.reason === 'offer_limit_reached' && 'Die vom Anfrager gewünschte Anzahl an Preisinformationen wurde bereits erreicht.'}
          </Alert>
        ) : (
          <SpaceBetween size="m">
            <Alert type="info">
              Der Anfrager erhält zunächst nur eine anonyme Preisinformation. Seine Firmendaten erscheinen erst nach der Qualifizierung des Vermieterangebots in Ihrem Portal.
            </Alert>
            <Form
              header={<Header variant="h2" description="Tragen Sie je Position den Netto-Einzelpreis ein. Zeilen- und Gesamtsummen werden automatisch berechnet.">Preisangebot abgeben</Header>}
              errorText={error}
              actions={<Button variant="primary" loading={submitting} onClick={submit}>Preisangebot übermitteln</Button>}
            >
              <form onSubmit={submit}>
                <SpaceBetween size="m">
                  <Table
                    wrapLines
                    items={priceRows}
                    header={<Header variant="h3">Preisangebotskalkulation</Header>}
                    empty="Keine Preisangebotspositionen vorhanden."
                    columnDefinitions={[
                      {
                        id: 'thumbnail',
                        header: 'Bild',
                        width: 82,
                        cell: (row) => row.lineType === 'operator_training'
                          ? <div className="offer-line-placeholder offer-line-placeholder--training" aria-label="Bedienerschulung">🎓</div>
                          : row.lineType === 'insurance'
                            ? <div className="offer-line-placeholder" aria-label="Versicherung"><span className="offer-line-insurance-shield" aria-hidden="true">✓</span></div>
                          : row.imageUrl
                            ? <img className="offer-line-thumbnail" src={resolveBackendAssetUrl(row.imageUrl)} alt={row.description} />
                            : (
                            <div
                              className={`offer-line-placeholder${row.lineType.includes('transport') ? ' offer-line-placeholder--transport' : ''}`}
                              aria-label={row.lineType === 'outbound_transport'
                                ? 'Hintransport'
                                : row.lineType === 'return_transport' ? 'Rücktransport' : 'SmartRent'}
                            >
                              {row.lineType === 'outbound_transport'
                                ? '→'
                                : row.lineType === 'return_transport' ? '←' : 'SR'}
                            </div>
                            ),
                      },
                      {
                        id: 'article',
                        header: 'Artikeltyp',
                        width: 260,
                        cell: (row) => (
                          <div className="offer-line-article">
                            <strong>{row.description}</strong>
                            {(row.quantity > 1 || row.lineType === 'operator_training') && (
                              <small>{row.lineType === 'operator_training' ? 'Teilnehmer' : 'Anzahl'}: {row.quantity}</small>
                            )}
                            {row.details && <small>{row.details}</small>}
                          </div>
                        ),
                      },
                      {
                        id: 'duration',
                        header: 'Mietdauer',
                        width: 220,
                        cell: (row) => row.lineType === 'insurance' ? (
                          <div className="offer-insurance-control">
                            <Select
                              placeholder="Versicherungstage wählen"
                              options={[
                                { value: 'calendar_days', label: `Durchberechnung – alle Kalendertage (${row.calendarDays})` },
                                { value: 'rental_days', label: `Nur Miettage (${row.rentalDays})` },
                              ]}
                              selectedOption={[
                                { value: 'calendar_days', label: `Durchberechnung – alle Kalendertage (${row.calendarDays})` },
                                { value: 'rental_days', label: `Nur Miettage (${row.rentalDays})` },
                              ].find((option) => option.value === row.calculationBasis) || null}
                              onChange={({ detail }) => updateLine(row.key, { calculationBasis: detail.selectedOption.value })}
                            />
                            <small>Versicherungszeitraum</small>
                          </div>
                        ) : row.lineType === 'rental'
                          ? `${row.durationDays} Miettag${row.durationDays === 1 ? '' : 'e'}`
                          : row.lineType === 'operator_training' ? '1 Schulung' : '1 Fahrt',
                      },
                      { id: 'from', header: 'Von', width: 105, cell: (row) => formatDateDe(row.dateFrom) },
                      { id: 'to', header: 'Bis', width: 105, cell: (row) => formatDateDe(row.dateTo) },
                      {
                        id: 'unitPrice',
                        header: 'Satz / Einzelpreis netto',
                        width: 250,
                        cell: (row) => row.lineType === 'insurance' ? (
                          <div className="offer-insurance-control">
                            <Select
                              placeholder="Berechnungsart wählen"
                              options={insurancePricingOptions}
                              selectedOption={insurancePricingOptions.find((option) => option.value === row.pricingMode) || null}
                              onChange={({ detail }) => updateLine(row.key, { pricingMode: detail.selectedOption.value, rateValue: '' })}
                            />
                            <Input
                              type="text"
                              inputMode="decimal"
                              value={row.rateValue}
                              onChange={({ detail }) => updateLine(row.key, { rateValue: detail.value })}
                              placeholder={row.pricingMode === 'percent_of_rent' ? 'z. B. 10,00' : '0,00'}
                              disabled={!row.pricingMode}
                            />
                            <small>{row.pricingMode === 'percent_of_rent' ? '% vom Mietpreis' : '€ pro Versicherungstag'}</small>
                          </div>
                        ) : (
                          <div className="offer-price-input">
                            <Input
                              type="text"
                              inputMode="decimal"
                              value={row.unitPriceNet}
                              onChange={({ detail }) => updateLine(row.key, { unitPriceNet: detail.value })}
                              placeholder="0,00"
                            />
                            <small>€ {row.unitLabel}</small>
                          </div>
                        ),
                      },
                      {
                        id: 'total',
                        header: 'Netto gesamt',
                        width: 145,
                        cell: (row) => {
                          const total = lineTotal(row, priceRows);
                          return <strong>{total === null ? '–' : formatCurrency(total)}</strong>;
                        },
                      },
                    ]}
                  />
                  <Box textAlign="right" fontSize="heading-m" fontWeight="bold">
                    Netto-Gesamtsumme: {priceSummary.valid ? formatCurrency(priceSummary.total) : '–'}
                  </Box>
                  <Alert type="info">Versicherung und Transport können mit 0,00 beziehungsweise 0 % als inklusive angegeben werden. Mietpreise müssen größer als 0,00 € sein.</Alert>
                  <FormField
                    label="Preisbindung"
                    description="Wie lange sind die angegebenen Preise ab Übermittlung des Preisangebots verbindlich?"
                  >
                    <Select
                      placeholder="Bitte auswählen"
                      options={priceBindingOptions}
                      selectedOption={priceBindingOptions.find((option) => option.value === priceBindingHours) || null}
                      onChange={({ detail }) => setPriceBindingHours(detail.selectedOption.value)}
                    />
                  </FormField>
                  <FormField
                    label="Interner Hinweis zum Preisangebot (optional)"
                    description="Wird nicht in der anonymisierten Preisinformation angezeigt."
                  >
                    <Textarea value={message} onChange={({ detail }) => setMessage(detail.value)} placeholder="z. B. interne Kalkulationsnotiz" />
                  </FormField>
                </SpaceBetween>
              </form>
            </Form>
          </SpaceBetween>
        )}
      </SpaceBetween>
    </div>
  );
}
